/**
 * UN/ECE Recommendation 20 unit code mappings
 * Maps German unit names to standard codes
 */

/**
 * Mapping of common German units to UN/ECE codes
 */
export const UNIT_CODE_MAP: Record<string, string> = {
    // Time units
    'h': 'HUR',
    'Stunde': 'HUR',
    'Stunden': 'HUR',
    'Std': 'HUR',
    'Std.': 'HUR',
    'min': 'MIN',
    'Minute': 'MIN',
    'Minuten': 'MIN',

    // Piece/unit
    'Stk': 'H87',
    'Stk.': 'H87',
    'Stuck': 'H87',
    'St': 'H87',
    'St.': 'H87',
    'Einheit': 'H87',

    // Length
    'm': 'MTR',
    'Meter': 'MTR',
    'cm': 'CMT',
    'Zentimeter': 'CMT',
    'mm': 'MMT',
    'Millimeter': 'MMT',
    'km': 'KMT',
    'Kilometer': 'KMT',

    // Weight
    'kg': 'KGM',
    'Kilogramm': 'KGM',
    'g': 'GRM',
    'Gramm': 'GRM',
    't': 'TNE',
    'Tonne': 'TNE',

    // Volume
    'l': 'LTR',
    'Liter': 'LTR',
    'ml': 'MLT',
    'Milliliter': 'MLT',
    'm3': 'MTQ',
    'Kubikmeter': 'MTQ',

    // Area
    'm2': 'MTK',
    'Quadratmeter': 'MTK',
    'qm': 'MTK',

    // Lump sum / flat rate - use C62 (one) as LS is not in XRechnung code list
    'pauschal': 'C62',
    'Pauschale': 'C62',
    'psch': 'C62',
    'psch.': 'C62',

    // Set
    'Set': 'SET',
    'Satz': 'SET',

    // Package
    'Paket': 'PK',
    'Pkg': 'PK',
    'Pkg.': 'PK',
};

/**
 * Map a unit string to UN/ECE code
 * @param unit - Unit string in German or abbreviation
 * @returns UN/ECE code, defaults to H87 (piece) if not found
 */
export function mapUnitToCode(unit?: string): string {
    if (!unit) {
        return 'H87'; // Default to piece
    }

    // Try direct lookup
    const directMatch = UNIT_CODE_MAP[unit];
    if (directMatch) {
        return directMatch;
    }

    // Try lowercase lookup
    const lowerMatch = UNIT_CODE_MAP[unit.toLowerCase()];
    if (lowerMatch) {
        return lowerMatch;
    }

    // Try trimmed lookup
    const trimmed = unit.trim();
    const trimmedMatch = UNIT_CODE_MAP[trimmed];
    if (trimmedMatch) {
        return trimmedMatch;
    }

    // Default to piece
    return 'H87';
}

/**
 * Get the unit name for a UN/ECE code
 */
export const UNIT_CODE_NAMES: Record<string, string> = {
    'HUR': 'hour',
    'MIN': 'minute',
    'H87': 'piece',
    'C62': 'one (lump sum)',
    'MTR': 'metre',
    'CMT': 'centimetre',
    'MMT': 'millimetre',
    'KMT': 'kilometre',
    'KGM': 'kilogram',
    'GRM': 'gram',
    'TNE': 'tonne',
    'LTR': 'litre',
    'MLT': 'millilitre',
    'MTQ': 'cubic metre',
    'MTK': 'square metre',
    'SET': 'set',
    'PK': 'package',
};
